import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import { Container, CircularProgress, Box, Alert, Snackbar } from '@mui/material'
import { getConnection, fetchPRSheetData, getDefaultAIPrompt, getCurrentUser } from '../services/salesforce'
import type { PRAIPrompt } from '../types/salesforce'
import PRSheetEditor from '../components/PRSheetEditor'
import AIGenerationDialog from '../components/AIGenerationDialog'
import GoogleAuthDialog from '../components/GoogleAuthDialog'
import { EditorAppBar } from '../components/EditorAppBar'
import { PreviewDialog } from '../components/PreviewDialog'
import { PDFSuccessDialog } from '../components/PDFSuccessDialog'
import { SupportDialog } from '../components/SupportDialog'

// カスタムフック
import { useEditorState } from '../hooks/useEditorState'
import { useSaveHandler } from '../hooks/useSaveHandler'
import { usePDFGenerator } from '../hooks/usePDFGenerator'
import { useAIGeneration } from '../hooks/useAIGeneration'
import { useGoogleCalendarSync } from '../hooks/useGoogleCalendarSync'
import { useAutoSave } from '../hooks/useAutoSave'
import { extractFixedSkillsFromPRSkills, extractLanguageSkills } from '../utils/skillsHelper'

export default function EditorPage() {
  const { careerId } = useParams<{ careerId?: string }>()
  const navigate = useNavigate()

  // セッションクリアしてログイン画面に戻る（CareerIDは保持）
  const handleSessionExpired = useCallback(() => {
    localStorage.removeItem('sf_access_token')
    localStorage.removeItem('sf_instance_url')
    navigate('/')
  }, [navigate])

  // ローディング・スナックバー
  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity })
  }, [])

  // EditorStateフック（PRSheetデータ、スキル、削除IDリスト管理）
  const editorState = useEditorState(null)

  // refを常に最新の値に更新（stale closure回避）
  const dataRef = useRef(editorState.data)
  const fixedSkillRatingsRef = useRef(editorState.fixedSkillRatings)
  const languageSkillsRef = useRef(editorState.languageSkills)

  useEffect(() => {
    dataRef.current = editorState.data
  }, [editorState.data])

  useEffect(() => {
    fixedSkillRatingsRef.current = editorState.fixedSkillRatings
  }, [editorState.fixedSkillRatings])

  useEffect(() => {
    languageSkillsRef.current = editorState.languageSkills
  }, [editorState.languageSkills])

  // AIプロンプト管理
  const [aiPrompt, setAiPrompt] = useState<PRAIPrompt | null>(null)

  // ユーザー情報
  const [userName, setUserName] = useState<string | null>(null)
  const [supportDialogOpen, setSupportDialogOpen] = useState(false)

  // SaveHandlerフック（保存ロジック）
  const saveHandler = useSaveHandler(
    dataRef,
    fixedSkillRatingsRef,
    languageSkillsRef,
    editorState.deletedSkillIds,
    editorState.deletedExperienceIds,
    editorState.deletedPortfolioItemIds,
    editorState.deletedHighlightEpisodeIds,
    editorState.deletedAwardIds,
    editorState.deletedEducationIds,
    editorState.deletedLinkIds,
    (refreshedData) => {
      editorState.setData(refreshedData)
      showSnackbar('保存しました', 'success')
    },
    (message, isSessionExpired) => {
      if (isSessionExpired) {
        showSnackbar('セッションが切れました。ログイン画面に戻ります', 'error')
        handleSessionExpired()
      } else {
        showSnackbar(message, 'error')
      }
    },
    editorState.clearDeletedIds,
  )

  // AutoSaveフック（自動保存タイマー）
  const autoSaveHook = useAutoSave(() => {
    const conn = getConnection()
    if (conn) {
      saveHandler.handleSave(conn)
    }
  })

  // PDFGeneratorフック（PDF生成・プレビュー）
  const pdfGenerator = usePDFGenerator(editorState.data, editorState.setData, (message, isSessionExpired) => {
    if (isSessionExpired) {
      showSnackbar('セッションが切れました。ログイン画面に戻ります', 'error')
      handleSessionExpired()
    } else {
      showSnackbar(message, 'error')
    }
  })

  // AIGenerationフック（AI生成）
  const aiGeneration = useAIGeneration(
    editorState.data,
    aiPrompt,
    editorState.setData,
    (message) => showSnackbar(message, 'success'),
    (message, isSessionExpired) => {
      if (isSessionExpired) {
        showSnackbar('セッションが切れました。ログイン画面に戻ります', 'error')
        handleSessionExpired()
      } else {
        showSnackbar(message, 'error')
      }
    },
  )

  // GoogleCalendarSyncフック（Google Calendar同期）
  const handleGoogleCalendarSuccess = useCallback((message: string) => showSnackbar(message, 'success'), [showSnackbar])
  const handleGoogleCalendarError = useCallback((message: string) => showSnackbar(message, 'error'), [showSnackbar])
  const googleCalendarSync = useGoogleCalendarSync(handleGoogleCalendarSuccess, handleGoogleCalendarError)

  // 初回データ読み込み
  useEffect(() => {
    async function loadData() {
      // careerIdがない場合はエラー（通常は発生しない、ルーティングで保証）
      if (!careerId) {
        setLoading(false)
        showSnackbar('CareerIDが指定されていません', 'error')
        return
      }

      // careerIdをlocalStorageに保存（認証前でも保存）
      localStorage.setItem('sf_career_id', careerId)

      const conn = getConnection()
      if (!conn) {
        // 未認証の場合、ログイン画面にリダイレクト
        setLoading(false)
        navigate('/login')
        return
      }

      try {
        const prSheetData = await fetchPRSheetData(conn, careerId)
        editorState.setData(prSheetData)

        // PRSkillから固定スキルのレーティングと言語スキルを抽出
        const ratings = extractFixedSkillsFromPRSkills(prSheetData.skills)
        editorState.setFixedSkillRatings(ratings)
        const langSkills = extractLanguageSkills(prSheetData.skills)
        editorState.setLanguageSkills(langSkills)

        // Google Calendar認証チェックと自動同期
        await googleCalendarSync.checkAndSync(conn)
      } catch (error) {
        console.error('データ読み込みエラー:', error)

        // セッションエラーの場合はログイン画面にリダイレクト
        const errorMessage = error instanceof Error ? error.message : String(error)
        if (errorMessage.includes('INVALID_SESSION_ID') || errorMessage.includes('Session expired')) {
          handleSessionExpired()
          return
        }

        showSnackbar('データの読み込みに失敗しました', 'error')
      } finally {
        setLoading(false)
      }
    }

    loadData()
    // googleCalendarSync.checkAndSyncは初回のみ実行したいため依存配列から除外
    // （onSuccess/onError経由で参照が変わると無限ループになる）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careerId])

  // PROMPT-0006プロンプトを読み込み
  useEffect(() => {
    async function loadAIPrompt() {
      const conn = getConnection()
      if (!conn) return

      try {
        const prompt = await getDefaultAIPrompt(conn)
        if (prompt) {
          setAiPrompt(prompt)
        } else {
          console.warn('PROMPT-0006が見つかりません')
        }
      } catch (error) {
        console.error('AIプロンプト読み込みエラー:', error)
        // プロンプト読み込みエラーは致命的ではないので、エラーは表示しない
      }
    }

    loadAIPrompt()
  }, [])

  // ユーザー情報を取得
  useEffect(() => {
    async function loadUserInfo() {
      const conn = getConnection()
      if (!conn) return

      try {
        const user = await getCurrentUser(conn)
        if (user) {
          setUserName(user.name)
        }
      } catch (error) {
        console.error('ユーザー情報取得エラー:', error)
      }
    }

    loadUserInfo()
  }, [])

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showSnackbar(`${label}をクリップボードにコピーしました`, 'success')
    } catch (error) {
      console.error('Failed to copy:', error)
      showSnackbar('コピーに失敗しました', 'error')
    }
  }

  // 手動保存ボタン
  const handleManualSave = () => {
    const conn = getConnection()
    if (!conn) {
      showSnackbar('認証が切れています', 'error')
      handleSessionExpired()
      return
    }
    saveHandler.handleSave(conn)
  }

  // PDF生成ボタン
  const handleGeneratePDFClick = () => {
    const conn = getConnection()
    if (!conn) {
      showSnackbar('認証が切れています', 'error')
      handleSessionExpired()
      return
    }
    pdfGenerator.handleGeneratePDF(conn)
  }

  // AI生成ハンドラ
  const handleAIGenerateClick = (additionalPrompt: string, targetFields: string[]) => {
    const conn = getConnection()
    if (!conn) {
      showSnackbar('認証が切れています', 'error')
      handleSessionExpired()
      return
    }
    aiGeneration.handleAIGenerate(conn, additionalPrompt, targetFields)
  }

  // AI確定/破棄ハンドラ
  const handleConfirmAI = () => {
    const conn = getConnection()
    if (!conn) {
      showSnackbar('認証が切れています', 'error')
      handleSessionExpired()
      return
    }
    aiGeneration.handleConfirmAIGeneration(conn)
  }

  const handleDiscardAI = () => {
    const conn = getConnection()
    if (!conn) {
      showSnackbar('認証が切れています', 'error')
      handleSessionExpired()
      return
    }
    aiGeneration.handleDiscardAIGeneration(conn)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!editorState.data) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          データの読み込みに失敗しました
        </Alert>
      </Container>
    )
  }

  return (
    <>
      <EditorAppBar
        careerName={editorState.data.career.Name}
        confirmationMode={aiGeneration.confirmationMode}
        autoSave={autoSaveHook.autoSave}
        saving={saveHandler.saving}
        userName={userName}
        onAutoSaveChange={autoSaveHook.setAutoSave}
        onSave={handleManualSave}
        onAIGenerate={() => aiGeneration.setAiDialogOpen(true)}
        onPreview={() => pdfGenerator.setPreviewOpen(true)}
        onConfirmAI={handleConfirmAI}
        onDiscardAI={handleDiscardAI}
        onSupportOpen={() => setSupportDialogOpen(true)}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <PRSheetEditor
          data={editorState.data}
          onChange={(newData) => {
            // ユーザー操作開始時に進行中の保存をキャンセル
            saveHandler.abortSave()
            editorState.handleDataChange(newData, autoSaveHook.scheduleAutoSave)
          }}
          onBlur={autoSaveHook.handleBlurAutoSave}
          fixedSkillRatings={editorState.fixedSkillRatings}
          onFixedSkillRatingChange={(field, rating) => {
            saveHandler.abortSave()
            editorState.setFixedSkillRatings((prev) => ({
              ...prev,
              [field]: {
                ...prev[field],
                rating,
              },
            }))
            autoSaveHook.scheduleAutoSave()
          }}
          onFixedSkillDescriptionChange={(field, description) => {
            saveHandler.abortSave()
            editorState.setFixedSkillRatings((prev) => ({
              ...prev,
              [field]: {
                ...prev[field],
                description,
              },
            }))
            autoSaveHook.scheduleAutoSave()
          }}
          languageSkills={editorState.languageSkills}
          onLanguageSkillsChange={(skills) => {
            saveHandler.abortSave()
            editorState.setLanguageSkills(skills)
            autoSaveHook.scheduleAutoSave()
          }}
          onLanguageSkillDelete={(skillId) => {
            saveHandler.abortSave()
            if (skillId && !skillId.startsWith('temp-')) {
              editorState.setDeletedSkillIds((prev) => [...prev, skillId])
            }
          }}
        />
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <PreviewDialog
        open={pdfGenerator.previewOpen}
        careerId={editorState.data.career.Id!}
        pdfContentDocumentId={editorState.data.prSheet.PDFContentDocumentId__c}
        instanceUrl={getConnection()?.instanceUrl}
        generatingPDF={pdfGenerator.generatingPDF}
        onClose={() => pdfGenerator.setPreviewOpen(false)}
        onGeneratePDF={handleGeneratePDFClick}
      />

      <PDFSuccessDialog
        open={pdfGenerator.pdfSuccessDialogOpen}
        pdfContentDocumentId={pdfGenerator.pdfContentDocumentId}
        instanceUrl={getConnection()?.instanceUrl}
        onClose={() => pdfGenerator.setPdfSuccessDialogOpen(false)}
      />

      {/* AI生成ダイアログ */}
      {aiPrompt && (
        <AIGenerationDialog
          open={aiGeneration.aiDialogOpen}
          onClose={() => aiGeneration.setAiDialogOpen(false)}
          onGenerate={handleAIGenerateClick}
          prompt={aiPrompt}
          loading={aiGeneration.aiLoading}
        />
      )}

      {/* Google Calendar認証ダイアログ */}
      <GoogleAuthDialog
        open={googleCalendarSync.googleAuthDialogOpen}
        onGetAuthUrl={async () => {
          const conn = getConnection()
          if (!conn) throw new Error('未認証です')
          return await googleCalendarSync.handleGetAuthUrl(conn)
        }}
        onClose={() => googleCalendarSync.setGoogleAuthDialogOpen(false)}
        onAuthComplete={async () => {
          const conn = getConnection()
          if (!conn) throw new Error('未認証です')
          await googleCalendarSync.handleAuthComplete(conn)
        }}
      />

      <SupportDialog
        open={supportDialogOpen}
        accessToken={localStorage.getItem('sf_access_token') || ''}
        careerId={editorState.data.career.Id || ''}
        onClose={() => setSupportDialogOpen(false)}
        onCopy={handleCopyToClipboard}
      />
    </>
  )
}
